// SkillSelector - Content generation template selector component
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Sparkles, Check } from 'lucide-react';
import { SKILL_CATEGORIES } from '../../../services/skillService';
import './SkillSelector.css';

const flattenDefaultSkills = () => {
    const list = [];
    Object.values(SKILL_CATEGORIES).forEach((category) => {
        category.skills.forEach((skill) => {
            list.push({
                id: skill.id,
                name: skill.name,
                description: skill.description,
                group: category.name,
            });
        });
    });
    return list;
};

const SkillSelector = ({
    value,
    onChange,
    label = '',
    options = [],
    disabled = false,
    size = 'default', // 'small' | 'default' | 'large'
    direction = 'down', // 'down' | 'up'
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const skillOptions = useMemo(
        () => (Array.isArray(options) && options.length > 0 ? options : flattenDefaultSkills()),
        [options]
    );

    const selectedSkill = useMemo(
        () => skillOptions.find((option) => option.id === value) || null,
        [skillOptions, value]
    );

    const groupedOptions = useMemo(() => {
        const groups = {};
        skillOptions.forEach((option) => {
            const key = option.group || '模板';
            if (!groups[key]) groups[key] = [];
            groups[key].push(option);
        });
        return groups;
    }, [skillOptions]);

    const handleSelect = (skillId) => {
        onChange(skillId);
        setIsOpen(false);
    };

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e) => {
            if (!e.target.closest('.skill-selector')) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const sizeClasses = {
        small: 'h-8 text-sm px-3',
        default: 'h-10 text-base px-4',
        large: 'h-12 text-lg px-5',
    };

    return (
        <div className={`skill-selector relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    w-full ${sizeClasses[size]} rounded-[14px]
                    bg-[#F7F8FA] border border-transparent
                    hover:border-blue-200 hover:bg-white
                    focus:outline-none focus:border-blue-300
                    flex items-center justify-between gap-2
                    transition-all duration-200
                `}
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    {label && <span className="text-gray-400 whitespace-nowrap">{label}</span>}
                    <span className="text-[#2563EB] font-bold truncate">
                        {selectedSkill ? selectedSkill.name : '选择模板'}
                    </span>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className={`absolute ${direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'} left-0 right-0 z-50 bg-white rounded-xl border border-gray-200 shadow-xl animate-in fade-in slide-in-from-${direction === 'up' ? 'bottom' : 'top'}-2 duration-200 overflow-hidden`}>
                    <div className="max-h-72 overflow-y-auto p-2">
                        {Object.entries(groupedOptions).map(([groupName, groupItems]) => (
                            <div key={groupName} className="mb-2 last:mb-0">
                                <div className="px-2 py-1 text-xs text-gray-400">{groupName}</div>
                                {groupItems.map((skill) => (
                                    <button
                                        key={skill.id}
                                        onClick={() => handleSelect(skill.id)}
                                        className={`
                      w-full text-left p-3 rounded-lg
                      hover:bg-gray-50 transition-colors
                      flex items-start gap-3
                      ${value === skill.id ? 'bg-blue-50 ring-1 ring-blue-200' : ''}
                    `}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-gray-800 truncate">{skill.name}</div>
                                            <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{skill.description}</div>
                                        </div>
                                        {value === skill.id && (
                                            <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SkillSelector;
